/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, IconButton, Tooltip } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { styled } from '@mui/material/styles';
import { ChatMessageProps, ChatFile } from './types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { ToolUse, ToolUseObservation } from './ChatItems/ToolUse';
import { copyText } from '../../../common/utils';
import { marked } from 'marked';
import { useNavigate } from 'react-router-dom';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';

const MessageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  maxWidth: '70%',
  alignSelf: 'flex-start',
  '&.user': {
    alignItems: 'flex-end',
    alignSelf: 'flex-end',
  },
  '& .message-actions': {
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
  },
  '&:hover .message-actions': {
    opacity: 1,
  },
}));

const MessageTitle = styled(Typography)<{ error?: boolean }>(
  ({ theme, error }) => ({
    fontSize: '0.875rem',
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: error ? theme.palette.error.main : theme.palette.primary.main,
    '&::before': {
      content: error ? '"!"' : '""',
      width: error ? 'auto' : 4,
      height: error ? 'auto' : 4,
      backgroundColor: error ? 'transparent' : 'currentColor',
      color: error ? theme.palette.error.main : 'inherit',
      borderRadius: error ? 0 : '50%',
      fontWeight: error ? 'bold' : 'normal',
    },
  }),
);

const MessageBubble = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: '12px 12px 12px 0',
  backgroundColor: theme.palette.grey[200],
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'box-shadow 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  '&.user': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: '12px 12px 0 12px',
    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.12)',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.16)',
    },
  },
  '&.error': {
    border: `1px solid ${theme.palette.error.main}`,
    color: theme.palette.text.primary,
    boxShadow: '0 2px 8px rgba(211, 47, 47, 0.08)',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(211, 47, 47, 0.12)',
    },
  },
}));

const MessageFooter = styled(Box)(({ theme }) => ({
  fontSize: '11px',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
}));

const MessageActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));

const AttachmentBox = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  maxWidth: '80%',
}));

const AttachmentItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  maxWidth: '100%',
}));

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  onFileDownload,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isUser = message.sender === 'user';
  const isError = message.status === 'error';
  const formattedTime = new Date(message.timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  const handleFileDownload = (file: ChatFile) => {
    if (onFileDownload) {
      onFileDownload(file);
    } else {
      console.log('File download not implemented:', file);
    }
  };

  const messageBody = useMemo(
    () => (
      <div className="markdown-body">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={
            {
              think: ({ node, children, ...props }) => (
                <details className="group" open={true}>
                  <summary className="flex cursor-pointer select-none list-none items-center whitespace-nowrap pl-2 font-bold text-gray-500">
                    <div className="flex shrink-0 items-center">
                      <svg
                        className="mr-2 h-3 w-3 transition-transform duration-500 group-open:rotate-90"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 5l7 7-7 7"
                        ></path>
                      </svg>
                      {t('thinking.deepThinking')}
                    </div>
                  </summary>
                  <div className="ml-2 border-l border-gray-300 bg-gray-50 p-3 text-gray-500">
                    {children}
                  </div>
                </details>
              ),
              'tool-input': ({ node, children, ...props }) => {
                return (
                  <ToolUse toolName={message.title} toolInput={children} />
                );
              },
              'tool-observation': ({ node, children, ...props }) => {
                return (
                  <ToolUseObservation
                    toolName={message.title}
                    toolObservation={children}
                  />
                );
              },
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              ),
            } as any
          }
        >
          {message.content}
        </ReactMarkdown>
      </div>
    ),
    [message.content],
  );

  return (
    <MessageContainer className={isUser ? 'user' : ''}>
      <MessageBubble
        className={`${isUser ? 'user' : ''} ${isError ? 'error' : ''}`}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {!isUser && message.title && (
            <MessageTitle error={isError}>{message.title}</MessageTitle>
          )}
          <Typography
            variant="body1"
            sx={{
              textAlign: 'left',
            }}
          >
            {messageBody}
          </Typography>
        </Box>
      </MessageBubble>
      {message.files && message.files.length > 0 && (
        <AttachmentBox>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            {t('chat.messages.attachments')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              flexDirection: 'column',
            }}
          >
            {message.files.map((file) => {
              let FileIcon = InsertDriveFileIcon;
              const format = file.format.toLowerCase();
              if (format === 'pdf') {
                FileIcon = PictureAsPdfIcon;
              } else if (['jpg', 'jpeg', 'png', 'gif'].includes(format)) {
                FileIcon = ImageIcon;
              } else if (['doc', 'docx'].includes(format)) {
                FileIcon = DescriptionIcon;
              }

              return (
                <Tooltip title={file.name} key={file.id}>
                  <AttachmentItem onClick={() => handleFileDownload(file)}>
                    <FileIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {file.name}
                    </Typography>
                  </AttachmentItem>
                </Tooltip>
              );
            })}
          </Box>
        </AttachmentBox>
      )}
      <MessageFooter>
        <Typography variant="caption">{formattedTime}</Typography>
        <MessageActions className="message-actions">
          {message.content.includes('type="mdi_article"') && (
            <Tooltip title={t('chat.openInWriter')}>
              <ActionButton
                size="small"
                onClick={async () => {
                  navigate('/articles/new', {
                    state: {
                      content: await marked(
                        message.content.split('</think>').pop(),
                      ),
                    },
                  });
                }}
                aria-label={t('chat.openInWriter')}
              >
                <EditCalendarOutlinedIcon fontSize="inherit" />
              </ActionButton>
            </Tooltip>
          )}
          <Tooltip title={t('chat.copyToClipboard')}>
            <ActionButton
              size="small"
              onClick={() => copyText(message.content)}
              aria-label={t('chat.copyToClipboard')}
            >
              <ContentCopyIcon fontSize="inherit" />
            </ActionButton>
          </Tooltip>
        </MessageActions>
      </MessageFooter>
    </MessageContainer>
  );
};

export default ChatMessage;
